import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { UserService } from '@app/services';
import { Observable, of } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs';
import { GUARD_RESOLVE_TIMEOUT_MS } from './guard-timeout';

@Injectable({
  providedIn: "root"
})
export class AdminGuard  {
  private readonly _componentName = 'AdminGuard';

  constructor(readonly userService: UserService, readonly router: Router) {}

  /**
   * Wait until the initial auth check has settled before deciding. On a fresh
   * app load (e.g. a link opened in a new tab) login/admin state is resolved
   * asynchronously via a silent token acquisition; evaluating synchronously here
   * would reject a valid admin before that completes and bounce them to home.
   * A timeout guards against the token request hanging indefinitely: if the check
   * never settles we fall back to a safe deny rather than a blank, stuck page.
   */
  private canAccess(): Observable<boolean> {
    return this.userService.authResolved$.pipe(
      filter((resolved) => resolved !== null),
      take(1),
      map(() => this.userService.isLoggedIn && this.userService.isAdmin),
      timeout({ first: GUARD_RESOLVE_TIMEOUT_MS, with: () => of(false) })
    );
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canAccess();
  }
  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canAccess();
  }
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return this.canAccess();
  }
}
